import { THeaderType } from 'components/Header/Header'
import LinkWithLocale from 'components/LinkWithLocale'
import React, { FC } from 'react'
import arrowRightDropdownItem from 'assets/svg/arrowRightDropdownItem.svg'
import { localesWithoutKorean } from 'constants/locales'
import useTranslation from 'hooks/useTranslation'
import { TFunction } from 'src/providers/TranslateProvider/TranslateProvider'
import { BadgeType } from 'src/types'
import styled from 'styled-components'
import BadgeDropdown from '../BadgeDropdown'
import useLocale from 'hooks/useLocale'
import { DEFAULT_LOCALE, GATSBY_MAIN_URL } from '@constants'

const getOptions = (t: TFunction, locale: string) => {
  return [
    {
      name: t('Quick start guide'),
      text: t('Learn how to start trading on Margex in 60 seconds'),
      url:
        locale === DEFAULT_LOCALE
          ? `${GATSBY_MAIN_URL}/quick-start-guide`
          : `${GATSBY_MAIN_URL}/${locale}/quick-start-guide`,
      badgeType: BadgeType.Hot,
      includeLangsArr: localesWithoutKorean,
    },
    {
      name: t('Video tutorials'),
      text: t('Take a look at our video tutorials to familiarize yourself with trading on Margex'),
      isBlank: true,
      url: 'https://www.youtube.com/c/margex',
      badgeType: BadgeType.Popular,
    },
    {
      name: t('How to make a deposit'),
      text: t('Detailed information on how to make a deposit'),
      url:
        locale === DEFAULT_LOCALE ? `${GATSBY_MAIN_URL}/how-to-deposit` : `${GATSBY_MAIN_URL}/${locale}/how-to-deposit`,
      badgeType: BadgeType.Popular,
      includeLangsArr: localesWithoutKorean,
    },
    {
      name: t('How to buy crypto'),
      text: t('Explore all the ways to buy crypto'),
      url:
        locale === DEFAULT_LOCALE
          ? `${GATSBY_MAIN_URL}/where-to-buy-bitcoin`
          : `${GATSBY_MAIN_URL}/${locale}/where-to-buy-bitcoin`,
      badgeType: BadgeType.BestOffer,
      includeLangsArr: localesWithoutKorean,
    },
    {
      name: t('Trading on Margex'),
      text: t('Full platform overview and guides'),
      url:
        locale === DEFAULT_LOCALE
          ? `${GATSBY_MAIN_URL}/trading-interface-overview`
          : `${GATSBY_MAIN_URL}/${locale}/trading-interface-overview`,
      badgeType: null,
      includeLangsArr: localesWithoutKorean,
    },
    {
      name: t('Cross vs Isolated Margin'),
      text: t("What is the difference between cross and isolated margin? Let's find out."),
      url:
        locale === DEFAULT_LOCALE
          ? `${GATSBY_MAIN_URL}/en/cross-margin-vs-isolated-margin`
          : `${GATSBY_MAIN_URL}/${locale}/cross-margin-vs-isolated-margin`,
      badgeType: null,
    },
    {
      name: t('Crypto Futures trading'),
      text: t('Everything you need to know about futures trading'),
      url:
        locale === DEFAULT_LOCALE
          ? `${GATSBY_MAIN_URL}/en/crypto-futures-trading`
          : `${GATSBY_MAIN_URL}/${locale}/crypto-futures-trading`,
      badgeType: null,
    },
    {
      name: t('How to short crypto'),
      text: t('A complete guide to shorting cryptocurrencies'),
      url:
        locale === DEFAULT_LOCALE
          ? `${GATSBY_MAIN_URL}/en/how-to-short-crypto`
          : `${GATSBY_MAIN_URL}/${locale}/how-to-short-crypto`,
      badgeType: null,
    },
    {
      name: t('Leverage Trading and Margin'),
      text: t('Detailed information about trading with leverage'),
      url:
        locale === DEFAULT_LOCALE
          ? `${GATSBY_MAIN_URL}/how-leverage-works`
          : `${GATSBY_MAIN_URL}/${locale}/how-leverage-works`,
      badgeType: null,
      includeLangsArr: localesWithoutKorean,
    },
  ]
}

interface DropdownExploreTradingProps extends IOwnProps {
  dropdownType:
    | 'app'
    | 'buy crypto'
    | 'futures'
    | 'spot'
    | 'explore trading'
    | 'price alerts'
    | 'staking'
    | 'markets'
    | 'refferal'
}

const DropdownExploreTrading: FC<DropdownExploreTradingProps> = () => {
  const { t } = useTranslation()
  const { locale } = useLocale()

  return (
    <>
      <DropdownItemGridContainer>
        <ItemWrapper>
          {getOptions(t, locale).map((item, index) => {
            const withLocale = item.url !== '/crypto-alerts'
            return (
              <DropdownItem
                key={index}
                to={item.url}
                target={item.isBlank ? '_blank' : undefined}
                withLocale={withLocale}
                includeLangsArr={item?.includeLangsArr}
                replace>
                <NameWrapper>
                  <DropdownItemName>{item.name}</DropdownItemName>
                  <BadgeDropdown type={item.badgeType} />
                </NameWrapper>
                <DropdownItemText>{item.text}</DropdownItemText>
              </DropdownItem>
            )
          })}
        </ItemWrapper>
      </DropdownItemGridContainer>
    </>
  )
}

export default DropdownExploreTrading

const DropdownItemGridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 0.875rem;
  column-gap: 2.75rem;
  background: linear-gradient(0deg, #f3f7fd, #f3f7fd), #123482;
  border-radius: 0.5rem;
`

const ItemWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: max-content;
  grid-auto-rows: max-content;
  grid-column-gap: 2.75rem;
  padding: 2rem;
`

const DropdownItem = styled(LinkWithLocale)`
  display: block;
  position: relative;
  width: 270px;
  padding: 0.625rem 0.75rem;
  clear: both;
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.normal};
  color: ${({ theme: { colors } }) => colors.dropdownItem};
  text-align: inherit;
  white-space: nowrap;
  background-color: transparent;
  border: 0;
  text-align: start;
  line-height: 1.375;
  transition: color 0.2s, background-color 0.2s;
  @media ${({ theme }) => theme.device.xxm} {
    width: 320px;
  }

  &:hover {
    > h6 {
      color: ${({ theme: { colors } }) => colors.link};
    }

    text-decoration: none;
    background-color: ${({ theme: { colors } }) => colors.white};

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      width: 2rem;
      border-radius: 0.25rem;
      background-image: url(${arrowRightDropdownItem});
      background-repeat: no-repeat;
      background-position: center;
      background-color: ${({ theme: { colors } }) => colors.newLightColor.vividBlue};

      [dir='rtl'] & {
        left: 0;
        right: unset;
        transform: rotate(180deg);
      }
    }
  }
`
const DropdownItemName = styled.span`
  color: ${({ theme: { colors } }) => colors.newLightColor.softNavy};
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.semibold};
  font-size: 0.875rem;
  margin-inline-end: 0.5rem;
`
const DropdownItemText = styled.p`
  width: 210px;
  margin: 0;
  margin-top: 2px;
  margin-inline-end: auto;
  white-space: pre-wrap;
  color: ${({ theme: { colors } }) => colors.newLightColor.softNavy};
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.normal};
  font-size: 0.875rem;
  opacity: 0.8;
  @media ${({ theme }) => theme.device.xxm} {
    width: 270px;
  }
`
const NameWrapper = styled.div`
  align-items: center;
  text-align: start;
`

interface IOwnProps {
  name?: string
  options: Array<{ name: string; url: string }>
  warning?: string | null
  type: THeaderType
}
